import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { formatClientName, getStatus } from '../utils';
import StatusSelect from './StatusSelect';

const DayPlanningDialog = ({
  selectedDay,
  reservations, // source unique : toutes les réservations
  isIntervenant,
  onClose,
  onStatusChange,
  onOpenDetails,
}) => {
  const dayReservations = selectedDay
    ? reservations.filter(
        (r) =>
          r.dateKey === selectedDay.jour.isoStr &&
          String(r.offreId) === String(selectedDay.offre.id)
      )
    : [];

  return (
    <Dialog
      open={!!selectedDay}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          borderBottom: '1px solid #F1F5F9',
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, color: '#1E293B' }}>
            Planning du jour
          </Typography>
          <Typography sx={{ color: '#64748B' }}>
            {selectedDay?.jour?.isoStr}
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent sx={{ p: 3 }}>
        {selectedDay && !dayReservations.length && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <EventBusyIcon sx={{ fontSize: 48, color: '#E2E8F0' }} />
            <Typography sx={{ color: '#94A3B8' }}>
              Aucune réservation prévue ce jour
            </Typography>
          </Box>
        )}

        {selectedDay && dayReservations.length > 0 && (
          <Stack spacing={2}>
            {dayReservations.map((res) => {
              const statusKey = res?.originalData?.status_reservation;
              const status = getStatus(statusKey);

              return (
                <Paper
                  key={res.id}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: '12px',
                    border: '1px solid #E2E8F0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: '0.2s',
                    '&:hover': { bgcolor: '#F8FAFC' },
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      bgcolor: status.color,
                    },
                  }}
                >
                  {/* INFOS */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                      <AccessTimeIcon sx={{ fontSize: 16, mr: 1 }} />
                      {res.heureDebut} — {res.heureFin}
                    </Typography>

                    <Typography sx={{ fontWeight: 600 }}>
                      {res.client
                        ? formatClientName(res.client, isIntervenant)
                        : 'Client inconnu'}
                    </Typography>

                    <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                      Intervenant : {res.intervenant}
                    </Typography>

                    <StatusSelect
                      value={statusKey}
                      onChange={(v) => onStatusChange(res.id, v)}
                      sx={{ mt: 1, width: '150px' }}
                    />
                  </Box>

                  {/* ACTION */}
                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<ChevronRightIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDetails(res, selectedDay.offre);
                    }}
                    sx={{
                      bgcolor: '#F1F5F9',
                      color: '#475569',
                      '&:hover': { bgcolor: '#3B82F6', color: '#fff' },
                    }}
                  >
                    Détails
                  </Button>
                </Paper>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DayPlanningDialog;
